/**
 * MyWorkStoppageFormModal
 *
 * A collab declaring — or, while HR has not processed it yet, correcting — their own arrêt de
 * travail: the leave type, the period (with its half days, as in Congés) and the
 * justificatifs. Nothing else: the motif, the CPAM figures and everything the suivi computes
 * are HR's to fill in when they validate it.
 *
 * Opened on a locked arrêt (validated or refused by HR), it is that arrêt's read-only view:
 * every field is disabled and the justificatifs can be opened but not changed. The API
 * refuses those writes anyway; the form only avoids offering them.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Langs, { Localize } from "../../app/lang/langs";
import { isAdmin, showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import NetworkManager from "../../app/managers/networkManager";
import {
  requestMyWorkStoppage, editMyWorkStoppage, getMyWorkStoppageAttachments,
  uploadMyWorkStoppageAttachment, deleteMyWorkStoppageAttachment, clearWorkStoppageAttachments,
} from "../../components/workStoppages/workStoppagesSlice";
import WorkStoppageAttachments from "../../components/workStoppages/WorkStoppageAttachments";
import FormDialog, { FormRow, FormButton } from "../../components/popups/FormDialog";
import LeaveDateRangeField, {
  leavePeriodFromTimestamps, isLeavePeriodValid,
} from "../../components/inputs/LeaveDateRangeField";
import LeaveTypeSelect from "../../components/inputs/LeaveTypeSelect";

const emptyForm = {
  id_sample_leave: "",
  start_date: "",
  start_period: "MORNING",
  end_date: "",
  end_period: "EVENING",
};

/** The API's refusal as a message — the lock in the collab's own language. */
export const myWorkStoppageError = (data) => (data?.locked
  ? Localize("MY_WORK_STOPPAGE_LOCKED")
  : data?.message || data?.error || Localize("WORK_STOPPAGE_SAVE_FAILED"));

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Function} props.onHide
 * @param {Object|null} props.leave - a row of `myList`, or null to declare a new arrêt
 * @param {Function} props.onSaved - called after a successful save, to refresh the list
 */
export default function MyWorkStoppageFormModal({ visible, onHide, leave, onSaved }) {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.settings.isMobile);
  const userRoleId = useSelector((state) => state.login.userRoleId);
  const sampleLeaves = useSelector((state) => state.sampleLeaves.list);
  const attachments = useSelector((state) => state.workStoppages.attachments);
  const attachmentsLoading = useSelector((state) => state.workStoppages.attachmentsLoading);

  const isEdit = !!leave;
  const locked = !!leave?.locked;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Justificatifs picked before the request exists; uploaded once the save returns its leave.
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    if (!visible) return;
    setForm(leave
      ? { id_sample_leave: leave.id_sample_leave ?? "", ...leavePeriodFromTimestamps(leave.from, leave.to) }
      : emptyForm);
    setErrors({});
    setPendingFiles([]);
    if (leave) dispatch(getMyWorkStoppageAttachments(leave.id));
    return () => { dispatch(clearWorkStoppageAttachments()); };
  }, [visible, leave, dispatch]);

  /**
   * The work-stoppage types, filtered the way Congés filters what a collab may request. The
   * arrêt's own type is always kept, so a read-only arrêt HR declared still shows its type.
   */
  const leaveTypes = useMemo(() => (sampleLeaves || [])
    .filter((sample) => sample.isWorkStoppage && (
      sample.id === leave?.id_sample_leave
      || (!sample.visibility && (isAdmin(userRoleId) || sample.roleId >= 2))))
    .sort((a, b) => String(a.label).localeCompare(String(b.label))),
  [sampleLeaves, userRoleId, leave]);

  const validate = () => {
    const next = {};
    if (!form.id_sample_leave) next.id_sample_leave = Localize("WORK_STOPPAGE_REQUIRED");
    if (!form.start_date) next.start_date = Localize("WORK_STOPPAGE_REQUIRED");
    if (!form.end_date) next.end_date = Localize("WORK_STOPPAGE_REQUIRED");
    else if (!isLeavePeriodValid(form)) next.end_date = Localize("ADD_LEAVE_ERROR_DATE");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (locked || !validate()) return;
    setSaving(true);
    try {
      const response = await dispatch(isEdit
        ? editMyWorkStoppage({ id: leave.id, ...form })
        : requestMyWorkStoppage(form));
      const data = response?.payload?.responseData;
      if (data && data.status === 1) {
        // The request is saved either way; a file that fails is reported, not a reason to undo.
        if (!isEdit && pendingFiles.length && data.id_planning) {
          for (const pending of pendingFiles) {
            const upload = await dispatch(uploadMyWorkStoppageAttachment({ file: pending.file, idPlanning: data.id_planning }));
            if (upload?.payload?.responseData?.status !== 1)
              showErrorToast(`${Localize("WORK_STOPPAGE_ATTACHMENT_FAILED")} (${pending.file.name})`);
          }
        }
        showSuccessToast(Localize(isEdit ? "WORK_STOPPAGE_SAVED" : "MY_WORK_STOPPAGE_SENT"));
        onSaved();
        onHide();
      } else {
        showErrorToast(myWorkStoppageError(data));
      }
    } catch (error) {
      showErrorToast(Localize("WORK_STOPPAGE_SAVE_FAILED"));
    } finally {
      setSaving(false);
    }
  };

  // ── Justificatifs ──────────────────────────────────────────────────────────
  // On a saved request they are on the server; while declaring they wait in `pendingFiles`.
  const attachmentItems = isEdit
    ? (attachments || []).map((attachment) => ({
      key: attachment.id,
      id: attachment.id,
      name: attachment.original_name,
      size: attachment.size,
      url: NetworkManager.getInstance().getMyWorkStoppageAttachmentUrl(attachment.id),
    }))
    : pendingFiles.map((pending) => ({ key: pending.key, name: pending.file.name, size: pending.file.size }));

  const addAttachment = async (file) => {
    if (!isEdit) {
      setPendingFiles((prev) => [...prev, { key: `${Date.now()}-${prev.length}`, file }]);
      return;
    }
    setUploading(true);
    try {
      const response = await dispatch(uploadMyWorkStoppageAttachment({ file, idPlanning: leave.id }));
      const data = response?.payload?.responseData;
      if (data?.status === 1) {
        showSuccessToast(Localize("WORK_STOPPAGE_ATTACHMENT_SAVED"));
        dispatch(getMyWorkStoppageAttachments(leave.id));
        onSaved();
      } else {
        showErrorToast(data?.locked ? Localize("MY_WORK_STOPPAGE_LOCKED") : (data?.error || Localize("WORK_STOPPAGE_ATTACHMENT_FAILED")));
      }
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (item) => {
    if (!isEdit) {
      setPendingFiles((prev) => prev.filter((pending) => pending.key !== item.key));
      return;
    }
    const response = await dispatch(deleteMyWorkStoppageAttachment({ id: item.id }));
    const data = response?.payload?.responseData;
    if (data?.status === 1) {
      showSuccessToast(Localize("WORK_STOPPAGE_ATTACHMENT_DELETED"));
      dispatch(getMyWorkStoppageAttachments(leave.id));
      onSaved();
    } else {
      showErrorToast(myWorkStoppageError(data));
    }
  };

  const titleKey = locked
    ? "MY_WORK_STOPPAGE_VIEW_TITLE"
    : (isEdit ? "MY_WORK_STOPPAGE_EDIT_TITLE" : "MY_WORK_STOPPAGE_REQUEST_TITLE");

  return (
    <FormDialog
      open={visible}
      onClose={onHide}
      title={<Langs str={titleKey} />}
      fullScreen={isMobile === 2}
      actions={locked ? (
        <FormButton variant="secondary" onClick={onHide}><Langs str="CLOSE" /></FormButton>
      ) : (
        <>
          <FormButton variant="secondary" onClick={onHide}><Langs str="Cancel" /></FormButton>
          <FormButton onClick={handleSubmit} disabled={saving}><Langs str="Save" /></FormButton>
        </>
      )}
    >
      <Alert severity="info" sx={{ borderRadius: "12px", mb: 1 }}>
        <Langs str={locked ? "MY_WORK_STOPPAGE_LOCKED" : "MY_WORK_STOPPAGE_REQUEST_HELP"} />
      </Alert>

      <FormRow label={<Langs str="TYPE" />} required>
        <LeaveTypeSelect
          value={form.id_sample_leave}
          onChange={(sample) => {
            setForm((prev) => ({ ...prev, id_sample_leave: sample ? sample.id : "" }));
            setErrors((prev) => ({ ...prev, id_sample_leave: "" }));
          }}
          options={leaveTypes}
          error={!!errors.id_sample_leave}
          helperText={errors.id_sample_leave}
          disabled={locked}
        />
      </FormRow>

      <LeaveDateRangeField
        value={form}
        onChange={(next) => {
          setForm((prev) => ({ ...prev, ...next }));
          setErrors((prev) => ({ ...prev, start_date: "", end_date: "" }));
        }}
        errors={errors}
        required
        disabled={locked}
      />

      <FormRow label={<Langs str="WORK_STOPPAGE_JUSTIFICATIFS" />} alignTop>
        <WorkStoppageAttachments
          items={attachmentItems}
          loading={isEdit && attachmentsLoading}
          busy={uploading}
          canEdit={!locked}
          onAdd={addAttachment}
          onRemove={removeAttachment}
          titleKey={null}
        />
      </FormRow>
    </FormDialog>
  );
}
